// OWNER: AREA-TEMPLATES (templates/*, partials/*). Components go in src/features/templates.
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FileCode2, Plus, Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { TemplateFormat, TemplateSummary } from '@/api/types'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/PageHeader'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Tooltip } from '@/components/ui/tooltip'
import { ownerFilters, useTemplatesList, type OwnerView } from '@/features/templates/api'
import { FormatBadge, OwnerLabel } from '@/features/templates/bits'
import { CreateTemplateDialog } from '@/features/templates/CreateTemplateDialog'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { useHotkeys } from '@/lib/hotkeys'
import { useAppScope } from '@/shell/useAppScope'

interface TemplatesSearch {
  q?: string
  owner?: OwnerView
  format?: TemplateFormat
}

export const Route = createFileRoute('/templates/')({
  validateSearch: (s: Record<string, unknown>): TemplatesSearch => ({
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    owner: s.owner === 'app' || s.owner === 'shared' ? s.owner : undefined,
    format: s.format === 'html' || s.format === 'mjml' ? s.format : undefined,
  }),
  component: TemplatesPage,
})

const col = tableColumns<TemplateSummary>()
const columns = col.columns([
  col.accessor('name', {
    header: 'Template',
    cell: (c) => (
      <div className="min-w-0">
        <div className="truncate font-medium text-ink">{c.getValue()}</div>
        <div className="truncate font-mono text-xs text-ink-3">{c.row.original.key}</div>
      </div>
    ),
    enableHiding: false,
  }),
  col.accessor((t) => t.application_id ?? '', {
    id: 'owner',
    header: 'Owner',
    cell: (c) => <OwnerLabel applicationId={c.row.original.application_id} className="text-sm" />,
    meta: { width: '13rem', label: 'Owner' },
  }),
  col.accessor('format', {
    header: 'Format',
    cell: (c) => <FormatBadge format={c.getValue()} />,
    meta: { width: '6rem' },
  }),
  col.accessor((t) => t.active_version ?? -1, {
    id: 'active',
    header: 'Active',
    cell: (c) => {
      const v = c.row.original.active_version
      return v != null ? (
        <span className="tabular font-mono text-sm text-ink">v{v}</span>
      ) : (
        <span className="text-sm text-ink-3">Unpublished</span>
      )
    },
    meta: { width: '7rem', label: 'Active version' },
  }),
  col.accessor((t) => (t.has_draft ? 1 : 0), {
    id: 'draft',
    header: 'Draft',
    cell: (c) => {
      const t = c.row.original
      if (!t.has_draft) return <span className="text-ink-3">—</span>
      return (
        <Tooltip content="Unpublished changes are waiting in the draft">
          <span className="inline-flex items-center gap-1.5">
            <Badge tone="brass">Draft</Badge>
            {t.draft_updated_at && (
              <RelativeTime value={t.draft_updated_at} className="text-xs text-ink-3" />
            )}
          </span>
        </Tooltip>
      )
    },
    meta: { width: '10rem' },
  }),
  col.accessor('updated_at', {
    header: 'Updated',
    cell: (c) => (
      <div className="text-right">
        <RelativeTime value={c.getValue()} className="text-ink-2" />
        <div className="truncate text-xs text-ink-3">{c.row.original.updated_by}</div>
      </div>
    ),
    sortFn: 'datetime',
    meta: { align: 'right', width: '10rem' },
  }),
])

function TemplatesPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = Route.useSearch()
  const { appId, application } = useAppScope()
  const { can } = usePermissions()
  const [text, setText] = useState(search.q ?? '')
  const q = useDebouncedValue(text.trim(), 250)
  const [creating, setCreating] = useState(false)
  const view: OwnerView = search.owner === 'app' && !appId ? 'all' : (search.owner ?? 'all')
  const list = useTemplatesList({
    ...ownerFilters(view, appId),
    q: q || undefined,
    format: search.format,
  })

  useEffect(() => {
    if ((search.q ?? '') !== q)
      void navigate({ search: (prev) => ({ ...prev, q: q || undefined }), replace: true })
  }, [search.q, q, navigate])

  useHotkeys([{ keys: 'c', handler: () => can('edit_content') && setCreating(true) }])

  const setSearch = (patch: Partial<TemplatesSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const ownerOptions = [
    {
      value: 'all' as const,
      label: application ? `${application.name} and shared` : 'All owners',
    },
    ...(appId
      ? [{ value: 'app' as const, label: `${application?.name ?? 'This application'} only` }]
      : []),
    { value: 'shared' as const, label: 'Shared library' },
  ]
  const filtered = !!q || view !== 'all' || !!search.format

  const createButton = (
    <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
      New template
    </Button>
  )

  return (
    <>
      <PageHeader
        title="Templates"
        description="Versioned email content. Rules pick a template; authors edit a draft, preview it against sample payloads, and publish."
        actions={
          <Can permission="edit_content" mode="disable">
            {createButton}
          </Can>
        }
      />
      <DataTable
        aria-label="Templates"
        columns={columns}
        data={list.items}
        getRowId={(t) => t.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        pagination={list.pagination}
        persistKey="templates"
        initialSorting={[{ id: 'updated_at', desc: true }]}
        onRowClick={(t) =>
          void navigate({ to: '/templates/$templateId', params: { templateId: t.id } })
        }
        toolbar={
          <div className="flex w-full flex-wrap items-center gap-2">
            <Input
              className="w-full sm:w-64"
              size="sm"
              placeholder="Search by name or key"
              aria-label="Search templates"
              value={text}
              onChange={(e) => setText(e.target.value)}
              leading={<Search />}
              trailing={
                text ? (
                  <IconButton
                    label="Clear search"
                    size="xs"
                    tooltip={false}
                    onClick={() => setText('')}
                  >
                    <X />
                  </IconButton>
                ) : undefined
              }
            />
            <Select<OwnerView>
              size="sm"
              className="w-full sm:w-56"
              aria-label="Owner"
              value={view}
              onValueChange={(v) => setSearch({ owner: v === 'all' ? undefined : v })}
              options={ownerOptions}
            />
            <Select<'any' | TemplateFormat>
              size="sm"
              className="w-full sm:w-32"
              aria-label="Format"
              value={search.format ?? 'any'}
              onValueChange={(v) => setSearch({ format: v === 'any' ? undefined : v })}
              options={[
                { value: 'any', label: 'Any format' },
                { value: 'html', label: 'HTML' },
                { value: 'mjml', label: 'MJML' },
              ]}
            />
          </div>
        }
        empty={
          filtered ? (
            <EmptyState
              compact
              icon={Search}
              title="No matching templates"
              description={
                q
                  ? `Nothing matches “${q}” with these filters.`
                  : 'No templates match these filters.'
              }
              action={
                <Button
                  size="sm"
                  onClick={() => {
                    setText('')
                    setSearch({ q: undefined, owner: undefined, format: undefined })
                  }}
                >
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={FileCode2}
              title="No templates yet"
              description={
                application
                  ? `Create the first template for ${application.name}, or a shared one every application can use.`
                  : 'A template is the subject, HTML, and text of one kind of email. Create one, then point a rule at it.'
              }
              action={can('edit_content') ? createButton : undefined}
            />
          )
        }
      />
      <CreateTemplateDialog open={creating} onOpenChange={setCreating} defaultOwner={appId} />
    </>
  )
}
